import React, { useEffect } from 'react';
import Select from 'react-select';
import cogoToast from 'cogo-toast';
import Modal from '../General/Modal';
import { createRecommendationRow, updateRecommendationRow } from '../../api/recommendations';
import { RowFormProvider, useRowForm } from './RowFormContext';
import { requiresCircleIndex, isValidCircleIndex } from './lib/row-definition-search';
import { RowDefinitionBuilderML } from './elements/RowDefinitionBuilderML';
import { RowDefinitionBuilderSearch } from './elements/RowDefinitionBuilderSearch';
import { RowPreviewSection } from './elements/RowPreviewSection';
import './RowFormModal.scss';

const SIZE_OPTIONS = ['small', 'medium', 'large'];
const ORIENTATION_OPTIONS = ['horizontal', 'vertical'];

const sizeOptions = SIZE_OPTIONS.map(size => ({ label: size, value: size }));
const orientationOptions = ORIENTATION_OPTIONS.map(orientation => ({ label: orientation, value: orientation }));

const RowFormModalContent = ({ close, onSuccess, editingRow = null, gridId = null }) => {
  const isEditMode = !!editingRow;

  const {
    title,
    setTitle,
    subtitle,
    setSubtitle,
    entity,
    size,
    setSize,
    orientation,
    setOrientation,
    showCounter,
    setShowCounter,
    filter,
    cardSort,
    definition,
    definitionType,
    setDefinitionType,
    isSubmitting,
    setIsSubmitting,
    initializeForm
  } = useRowForm();

  // Initialize form with editingRow data
  useEffect(() => {
    initializeForm(editingRow);
  }, [editingRow, initializeForm]);

  const handleSubmit = async e => {
    e.preventDefault();

    if (!title.trim()) {
      cogoToast.error('Please enter a title for the row');
      return;
    }

    if (!entity) {
      cogoToast.error('Please select a type');
      return;
    }

    if (definitionType === 'search' && requiresCircleIndex(definition) && !isValidCircleIndex(definition.circleIndex)) {
      cogoToast.error("Please set a circle index — Curator Group rows scoped to the user's circles need one");
      return;
    }

    try {
      setIsSubmitting(true);

      const rowData = {
        title: title.trim(),
        subtitle: subtitle.trim(),
        entity,
        display: {
          size,
          orientation,
          showCounter
        },
        filter,
        cardSort: cardSort?.trim(),
        definition,
        definitionType
      };

      // Only include RecommendationGrid_id when creating a new row
      if (!isEditMode && gridId) {
        rowData.RecommendationGrid_id = gridId;
      }

      if (isEditMode) {
        await updateRecommendationRow(editingRow.id, rowData);
        cogoToast.success('Recommendation row updated successfully');
      } else {
        await createRecommendationRow(rowData);
        cogoToast.success('Recommendation row created successfully');
      }

      onSuccess();
      close();
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} recommendation row:`, error);
      cogoToast.error(`Error ${isEditMode ? 'updating' : 'creating'} recommendation row`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal close={close} title={isEditMode ? 'Edit Recommendation Row' : 'Create New Recommendation Row'} className='create-row-modal'>
      <form onSubmit={handleSubmit}>
        <div className='form-content'>
          <div className='descriptors'>
            <div className='form-field'>
              <label htmlFor='title'>Title</label>
              <input
                id='title'
                name='title'
                type='text'
                placeholder='Enter row title'
                value={title}
                onChange={e => setTitle(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className='form-field'>
              <label htmlFor='subtitle'>Subtitle</label>
              <input id='subtitle' name='subtitle' type='text' value={subtitle} onChange={e => setSubtitle(e.target.value)} disabled={isSubmitting} />
            </div>
          </div>

          <div className='display'>
            <div className='form-field'>
              <label htmlFor='size'>Size</label>
              <Select
                id='size'
                name='size'
                options={sizeOptions}
                placeholder='Select size'
                onChange={option => setSize(option.value)}
                value={{ label: size, value: size }}
                isDisabled={isSubmitting}
              />
            </div>

            <div className='form-field'>
              <label htmlFor='orientation'>Orientation</label>
              <Select
                id='orientation'
                name='orientation'
                options={orientationOptions}
                placeholder='Select orientation'
                onChange={option => setOrientation(option.value)}
                value={{ label: orientation, value: orientation }}
                isDisabled={isSubmitting}
              />
            </div>

            <div className='form-field checkbox-field'>
              <label htmlFor='showCounter'>Counter</label>

              <input
                id='showCounter'
                name='showCounter'
                type='checkbox'
                checked={showCounter}
                onChange={e => setShowCounter(e.target.checked)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className='form-field'>
            <label>Definition Type</label>
            <div>
              <div className='radio-group'>
                <label className='radio-label'>
                  <input
                    type='radio'
                    name='definitionType'
                    value='search'
                    checked={definitionType === 'search'}
                    onChange={() => setDefinitionType('search')}
                    disabled={isSubmitting}
                  />
                  <span>Search</span>
                </label>
                <label className='radio-label'>
                  <input
                    type='radio'
                    name='definitionType'
                    value='ML'
                    checked={definitionType === 'ML'}
                    onChange={() => setDefinitionType('ML')}
                    disabled={isSubmitting}
                  />
                  <span>ML</span>
                </label>
              </div>

              {definitionType === 'ML' ? <RowDefinitionBuilderML /> : <RowDefinitionBuilderSearch />}
            </div>
          </div>

          {definitionType === 'ML' && <RowPreviewSection />}
        </div>

        <div className='modal-actions'>
          <button type='button' className='cancel-button' onClick={close} disabled={isSubmitting}>
            Cancel
          </button>
          <button type='submit' className='create-button' disabled={isSubmitting}>
            {isSubmitting ? (isEditMode ? 'Updating...' : 'Creating...') : isEditMode ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export const RowFormModal = props => {
  return (
    <RowFormProvider>
      <RowFormModalContent {...props} />
    </RowFormProvider>
  );
};
